import { ApiMessages } from "../constants/Strings";
import { Entity } from "./Entities";
import { Response } from "./Responses";

const ApiUrl = process.env.REACT_APP_API_URL ?? `${process.env.PUBLIC_URL}/api/`;

/**
 * Perform a POST request on *route* with
 *  - json stringified *data* as part of a form with name 'jsonData'
 *  - files as other parts
 */
async function Post<T extends Response>(route: string, data: any, files: Record<string, File> = {}) {
  const formData = new FormData();
  formData.append("jsonData", JSON.stringify(data));
  Object.keys(files).forEach((f) => formData.append(f, files[f]));
  const opts = {
    method: "POST",
    body: formData,
  };
  return sendRequest<T>(route, opts);
}

/**
 * Perform a GET request on *route*
 */
async function Get<T extends Entity | Entity[]>(route: string): Promise<ApiRequestResult<T>> {
  const opts = {
    method: "GET",
  };
  return sendRequest(route, opts);
}

/**
 * Perform a PUT request on *route* with raw *data*
 */
async function Put(route: string, data: any) {
  const opts = {
    method: "PUT",
    body: data,
  };
  return sendRequest(route, opts);
}

/**
 * Perform a PATCH request on *route* with *data* as a json string
 */
async function Patch(route: string, data: any) {
  const opts = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return sendRequest(route, opts);
}

/**
 * Perform a DELETE request on *route*
 */
async function Delete(route: string) {
  const opts = {
    method: "DELETE",
  };
  return sendRequest(route, opts);
}

interface ApiRequestResult<T> {
  success: boolean;
  httpStatus: number;
  errorReason?: string;
  data?: T;
  redirection?: string;
}

async function sendRequest<T extends Entity | Entity[] | Response>(
  url: string,
  opts: RequestInit
): Promise<ApiRequestResult<T>> {
  if (process.env.REACT_APP_API_FORCE_INCLUDE_CREDS === "true") opts.credentials = "include";
  const result: ApiRequestResult<T> = {
    success: false,
    httpStatus: 0,
  };
  try {
    if (!/^https?:\/\//.test(url)) url = `${ApiUrl}${url}`;
    const response = await fetch(url, opts);
    result.httpStatus = response.status;
    if (response.status >= 200 && response.status < 300) {
      result.success = true;
    } else {
      result.success = false;
      result.errorReason = response.statusText;
    }

    if (parseInt(response.headers.get("Content-Length") ?? "") > 0) {
      const contentType = response.headers.get("Content-Type");
      if (result.success)
        if (contentType === "application/json") result.data = (await response.json()) as T;
        else result.success = false;
      if (!result.success) result.errorReason = await response.text();
    }

    if (response.headers.has("Location")) result.redirection = response.headers.get("Location") ?? undefined;
  } catch {
    result.success = false;
    result.errorReason = ApiMessages.networkError;
    result.data = undefined;
  }
  return result;
}

const Api = {
  Url: ApiUrl,
  Get,
  Post,
  Put,
  Patch,
  Delete,
};

export default Api;
